import type { TCIP30ErrorWithCode } from "@sundae/wallets/dist/types/cip30.types";
import { IComposedTx, ITxBuilderSubmit } from "@sundaeswap/core";

import { ITasteTestFees } from "@sundaeswap/taste-test";
import { Tx, TxComplete } from "lucid-cardano";
import {
  ETransactionErrorMessages,
  ETransactionErrorState,
  TAllConfigTypes,
} from "../../hooks/useTransactionErrorHandler";
import { ErrorHandler } from "./ErrorHandler.abstract.class";
import { EternlErrorHandler } from "./handlers/EternlErrorHandler";
import { GenericErrorHandler } from "./handlers/GenericErrorHandler.class";

interface ITransactionErrorHandlerArgs {
  error: TCIP30ErrorWithCode | string | Error;
  transactionState: ETransactionErrorState;
  config?: TAllConfigTypes;
  cbor?: string;
}

export class TransactionErrorHandler {
  handler: ErrorHandler;
  error: ITransactionErrorHandlerArgs["error"];
  config?: ITransactionErrorHandlerArgs["config"];
  cbor?: string;

  constructor({ error, transactionState, cbor, config }: ITransactionErrorHandlerArgs) {
    this.error = error;
    this.cbor = cbor;
    this.config = config;

    if ((error as TCIP30ErrorWithCode)?.code && (error as TCIP30ErrorWithCode)?.info) {
      this.handler = new EternlErrorHandler(error as TCIP30ErrorWithCode, transactionState);
    } else {
      this.handler = new GenericErrorHandler(
        ((error as Error)?.message || error) as string,
        transactionState
      );
    }
  }

  public isKnownError() {
    return Boolean(this?.handler) && Boolean(this.getCode());
  }

  public getCode() {
    return this?.handler?.getCode();
  }

  /**
   * Helper function to build the transaction without signing or submitting it.
   * For the majority of our transaction flows, we want to build the transaction upfront to:
   * - Display the transaction details to the user before they sign and submit it (tx fees, etc.)
   * - Check for tx building errors before the user signs and submits the transaction and render the proper error message
   */
  static async buildTransaction(
    buildFn: () => Promise<
      IComposedTx<Tx, TxComplete> | IComposedTx<Tx, TxComplete, string | undefined, ITasteTestFees>
    >
  ) {
    let tx:
      | IComposedTx<Tx, TxComplete>
      | IComposedTx<Tx, TxComplete, string | undefined, ITasteTestFees>
      | null
      | undefined;

    try {
      tx = await buildFn();
    } catch (e) {
      throw { error: e, transactionState: ETransactionErrorState.BUILD };
    }

    if (!tx) {
      throw {
        error: new Error(
          "Something went wrong while building this transaction. Please try again or contact support."
        ),
        transactionState: ETransactionErrorState.BUILD,
      };
    }

    try {
      const buildTx = await tx.build();
      console.log(buildTx.cbor);
      return tx;
    } catch (e) {
      let cbor = "";
      try {
        cbor = (await tx.build()).cbor;
      } catch (e) {}

      throw {
        error: e,
        transactionState: ETransactionErrorState.BUILD,
        cbor,
      };
    }
  }

  /**
   * Helper function to automatically handle error reporting when
   * building a transaction in any flow state, based on the SDK.
   * For the majority of our transaction flows, we're building the transaction upfront to render the proper calculated fees (cardanoTxFee, scooperFee, deposit, etc.), so if the `builtTx` is passed in, we skip the additional build step.
   */
  static async handleSDKMutation(
    buildFn?: () => Promise<
      | IComposedTx<Tx, TxComplete>
      | IComposedTx<Tx, TxComplete, string | undefined, ITasteTestFees>
      | null
      | undefined
    >,
    builtTx?:
      | IComposedTx<Tx, TxComplete>
      | IComposedTx<Tx, TxComplete, string | undefined, ITasteTestFees>,
    onBuiltCallback?: () => void
  ) {
    let tx:
      | IComposedTx<Tx, TxComplete>
      | IComposedTx<Tx, TxComplete, string | undefined, ITasteTestFees>
      | null
      | undefined;
    try {
      tx = builtTx ?? (await buildFn?.());
    } catch (e) {
      throw { error: e, transactionState: ETransactionErrorState.BUILD };
    }

    if (!tx) {
      throw {
        error: new Error(
          "Something went wrong while building this transaction. Please try again or contact support."
        ),
        transactionState: ETransactionErrorState.BUILD,
      };
    }

    let data: ITxBuilderSubmit;
    try {
      const maxCardanoFee = 10000000n; // 10 ADA
      if (tx.fees.cardanoTxFee && tx.fees.cardanoTxFee.amount > maxCardanoFee) {
        throw new Error(ETransactionErrorMessages.FEE_EXCEEDS_MAXIMUM);
      }

      onBuiltCallback?.();
      const buildTx = await tx.build();
      console.log(buildTx.cbor);
      data = await buildTx.sign();
    } catch (e) {
      let cbor = "";
      try {
        cbor = (await tx.build()).cbor;
      } catch (e) {}

      throw {
        error: e,
        transactionState: ETransactionErrorState.SIGN,
        cbor,
      };
    }

    try {
      return await data.submit();
    } catch (e) {
      throw { error: e, transactionState: ETransactionErrorState.SEND, cbor: data.cbor };
    }
  }
}
